import React from "react";

/* =======================
   Color Utilities
======================= */

const hexToRgb = (hex) => {
  const parsed = hex.replace("#", "");
  return {
    r: parseInt(parsed.substring(0, 2), 16),
    g: parseInt(parsed.substring(2, 4), 16),
    b: parseInt(parsed.substring(4, 6), 16),
  };
};

const interpolateColor = (c1, c2, factor) => ({
  r: Math.round(c1.r + factor * (c2.r - c1.r)),
  g: Math.round(c1.g + factor * (c2.g - c1.g)),
  b: Math.round(c1.b + factor * (c2.b - c1.b)),
});

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;

/* =======================
   Gradient Stops
======================= */

const COLOR_STOPS = [
  { value: 0, color: "#ef4444" }, // red
  { value: 25, color: "#f97316" }, // orange
  { value: 50, color: "#eab308" }, // yellow
  { value: 75, color: "#22c55e" }, // green
  { value: 100, color: "#16a34a" }, // dark green
];

const getSmoothColor = (value) => {
  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    const start = COLOR_STOPS[i];
    const end = COLOR_STOPS[i + 1];

    if (value >= start.value && value <= end.value) {
      const factor = (value - start.value) / (end.value - start.value);

      const interpolated = interpolateColor(
        hexToRgb(start.color),
        hexToRgb(end.color),
        factor,
      );

      return rgbToHex(interpolated);
    }
  }

  return COLOR_STOPS[COLOR_STOPS.length - 1].color;
};

/* =======================
   Component
======================= */

const CircularProgressBar = ({
  value = 0,
  strokeWidth = 10,
  bgColor = "#e5e7eb",
  showLabel = true,
}) => {
  const size = 100; // virtual canvas
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const clampedValue = Math.min(100, Math.max(0, value));
  const offset = circumference - (clampedValue / 100) * circumference;

  const progressColor = getSmoothColor(clampedValue);

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width="100%"
      height="100%"
      style={{ display: "block" }}
    >
      {/* Background */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={bgColor}
        strokeWidth={strokeWidth}
        fill="none"
      />

      {/* Progress */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={progressColor}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{
          transition: "stroke-dashoffset 0.6s ease, stroke 0.4s ease",
        }}
      />

      {/* Label */}
      {showLabel && (
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          fontSize="20"
          fontWeight="600"
          fill={progressColor}
        >
          {clampedValue}%
        </text>
      )}
    </svg>
  );
};

export default CircularProgressBar;
